import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../layouts/Header';
import { Col, Row, Button, Table, Accordion, Modal, Form, InputGroup, ListGroup, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    Clock,
    Calendar,
    CurrencyRupee,
    BoxSeam,
    CheckCircle,
    CalendarEvent,
    ShopWindow,
    CartPlus,
    CupHot,
    Pencil,
    Trash,
    HourglassSplit,
    ListCheck,
    Gem,
    Eye,
    EyeSlash,
    PauseCircleFill,
    Search,
    ArrowRepeat,
    Plus,
    ChevronLeft,
    ChevronRight
} from 'react-bootstrap-icons';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useSelector } from 'react-redux';

const PAGE_SIZE = 5;

/* ------------------------------------------------------------------
   Styles (kept in this file, scoped under .sub-page)
------------------------------------------------------------------- */
const subStyles = `
.sub-page { width: 100%; max-width: 100%; }

/* Hero */
.sub-page .sub-hero {
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; flex-wrap: wrap; min-height: 120px;
    padding: 14px 22px; border-radius: 13px; color: #fff;
    background:
        radial-gradient(circle at 78% 20%, rgba(129,140,248,.35), transparent 45%),
        linear-gradient(110deg, #3b3f8f 0%, #2b2d6e 55%, #1e1b4b 100%);
    box-shadow: 0 8px 24px rgba(49,46,129,.25);
}
.sub-page .sub-hero__left { display: flex; align-items: center; gap: 16px; }
.sub-page .sub-hero__icon {
    width: 48px; height: 48px; border-radius: 14px;
    display: grid; place-items: center; font-size: 26px;
    background: linear-gradient(135deg, #8b5cf6, #6366f1);
    box-shadow: 0 6px 16px rgba(99,102,241,.45);
}
.sub-page .sub-hero__title { margin: 0; font-size: 20px; font-weight: 700; color: #fff; }
.sub-page .sub-hero__subtitle { margin: 2px 0 0; font-size: 13px; color: rgba(255,255,255,.8); }
.sub-page .sub-hero__actions { display: flex; gap: 12px; }

/* Buttons */
.sub-page .sub-btn {
    display: inline-flex; align-items: center; gap: 8px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 7px 15px; font-size: 12.5px; font-weight: 600;
    cursor: pointer; transition: all .2s ease;
}
.sub-page .sub-btn--primary { background: #4f46e5; color: #fff; box-shadow: 0 4px 14px rgba(79,70,229,.45); }
.sub-page .sub-btn--primary:hover { background: #4338ca; transform: translateY(-1px); }
.sub-page .sub-btn--soft { background: #f1f5fd; color: #1e293b; border-color: #e8ecf4; }
.sub-page .sub-btn--soft:hover { background: #e6ecfa; }
.sub-page .sub-btn--outline { background: #fff; color: #4f46e5; border-color: #4f46e5; padding: 7px 18px; }
.sub-page .sub-btn--outline:hover { background: #4f46e5; color: #fff; }

.sub-page .sub-icon-btn {
    width: 36px; height: 36px; display: grid; place-items: center;
    border-radius: 8px; border: 1px solid #dbe1ee;
    background: #fff; color: #4f46e5; cursor: pointer; transition: all .2s ease;
}
.sub-page .sub-icon-btn:hover { background: #eef2ff; }
.sub-page .sub-icon-btn--danger { color: #ef4444; border-color: #fca5a5; background: #fff5f5; }
.sub-page .sub-icon-btn--danger:hover { background: #ef4444; color: #fff; }

/* Stats */
.sub-page .sub-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 14px 0; }
.sub-page .sub-stat {
    display: flex; align-items: center; gap: 14px;
    padding: 10px 16px; border-radius: 14px; border: 1px solid #e8ecf4; height: 80px; transition: all .2s ease;
}
.sub-page .sub-stat__icon {
    width: 44px; height: 44px; border-radius: 12px;
    display: grid; place-items: center; font-size: 22px;
}
.sub-page .sub-stat__value { font-size: 22px; font-weight: 700; line-height: 1.1; }
.sub-page .sub-stat__label { font-size: 14px; color: #64748b; }

.sub-page .sub-stat--total { background: linear-gradient(135deg, #f4f6ff, #eaeeff); }
.sub-page .sub-stat--total .sub-stat__icon { background: #dfe6ff; color: #4f46e5; }
.sub-page .sub-stat--total .sub-stat__value { color: #1e3a8a; }
.sub-page .sub-stat--active { background: linear-gradient(135deg, #f1fbf5, #e5f7ec); }
.sub-page .sub-stat--active .sub-stat__icon { background: #d3f3df; color: #16a34a; }
.sub-page .sub-stat--active .sub-stat__value { color: #166534; }
.sub-page .sub-stat--inactive { background: linear-gradient(135deg, #fffaf0, #fff3dc); }
.sub-page .sub-stat--inactive .sub-stat__icon { background: #fde9bf; color: #f59e0b; }
.sub-page .sub-stat--inactive .sub-stat__value { color: #ea580c; }
.sub-page .sub-stat--deleted { background: linear-gradient(135deg, #fff5f5, #ffe9e9); }
.sub-page .sub-stat--deleted .sub-stat__icon { background: #ffd6d6; color: #ef4444; }
.sub-page .sub-stat--deleted .sub-stat__value { color: #dc2626; }

/* Filters */
.sub-page .sub-filters {
    display: grid; grid-template-columns: 2.2fr 1fr 1fr 1.8fr auto;
    gap: 10px; margin-bottom: 14px;
}
.sub-page .sub-filters__search {
    display: flex; align-items: center; gap: 10px;
    background: #fff; border: 1px solid #e8ecf4; border-radius: 10px;
    padding: 0 14px; height: 42px; color: #64748b;
}
.sub-page .sub-filters__search input {
    border: 0; outline: 0; width: 100%; font-size: 14px; color: #1e293b; background: transparent;
}
.sub-page .sub-filters__search:focus-within { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.12); }
.sub-page .sub-filters__select {
    height: 42px; padding: 0 12px; border: 1px solid #e8ecf4; border-radius: 10px;
    background: #fff; font-size: 14px; color: #1e293b; outline: 0;
}
.sub-page .sub-filters__select:focus { border-color: #4f46e5; }
.sub-page .sub-filters__outlet .sub-select__control {
    min-height: 42px; border-radius: 10px; border-color: #e8ecf4; font-size: 14px; box-shadow: none;
}
.sub-page .sub-filters__outlet .sub-select__control--is-focused { border-color: #4f46e5; }

/* Cards */
.sub-page .sub-list { display: flex; flex-direction: column; gap: 12px; }
.sub-page .sub-card {
    background: #fff; border: 1px solid #e8ecf4; border-radius: 14px;
    padding: 10px; transition: box-shadow .2s ease;
}
.sub-page .sub-card:hover { box-shadow: 0 8px 22px rgba(30,41,59,.08); }
.sub-page .sub-card__row {
    display: grid;
    grid-template-columns: 280px minmax(0, 1.5fr) minmax(0, 1.4fr) auto;
    gap: 18px; align-items: center;
}
.sub-page .sub-card__media {
    position: relative; height: 150px; border-radius: 10px;
    overflow: hidden; background: #f1f5f9;
}
.sub-page .sub-card__media img { width: 100%; height: 100%; object-fit: cover; }
.sub-page .sub-card__media-fallback {
    width: 100%; height: 100%; display: grid; place-items: center; color: #94a3b8;
}
.sub-page .sub-card__status {
    position: absolute; top: 8px; left: 8px; padding: 2px 12px;
    border-radius: 8px; font-size: 12px; font-weight: 600; color: #fff;
    text-transform: capitalize;
}
.sub-page .sub-card__status--active { background: #16a34a; }
.sub-page .sub-card__status--inactive { background: #94a3b8; }
.sub-page .sub-card__title { margin: 0 0 2px; font-size: 16px; font-weight: 700; color: #1e293b; }
.sub-page .sub-card__subtitle { margin: 0 0 6px; font-size: 12.5px; color: #64748b; }
.sub-page .sub-card__desc {
    margin: 0 0 8px; font-size: 11.5px; color: #64748b; line-height: 1.45;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.sub-page .sub-card__tags { display: flex; flex-wrap: wrap; gap: 8px; }
.sub-page .sub-card__metrics {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px;
    padding: 0 18px; border-left: 1px solid #e8ecf4; border-right: 1px solid #e8ecf4;
}
.sub-page .sub-card__side {
    display: flex; flex-direction: column; justify-content: space-between;
    align-self: stretch; align-items: flex-end; gap: 14px; min-width: 260px;
}
.sub-page .sub-card__valid { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #64748b; }
.sub-page .sub-card__actions { display: flex; align-items: center; gap: 10px; }

.sub-page .sub-tag {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600;
}
.sub-page .sub-tag--blue { background: #e8f0ff; color: #2563eb; }
.sub-page .sub-tag--indigo { background: #eaeaff; color: #4f46e5; }
.sub-page .sub-tag--amber { background: #fff3cf; color: #d97706; }

.sub-page .sub-pill-green {
    background: #dcf5e4; color: #16a34a; padding: 4px 14px;
    border-radius: 999px; font-size: 13px; font-weight: 600;
}

.sub-page .sub-metric { display: flex; align-items: center; gap: 10px; color: #64748b; font-size: 18px; }
.sub-page .sub-metric div { display: flex; flex-direction: column; line-height: 1.2; }
.sub-page .sub-metric strong { font-size: 14px; color: #1e293b; font-weight: 600; }
.sub-page .sub-metric span { font-size: 12px; color: #64748b; }
.sub-page .sub-metric--wide { grid-column: span 2; }

/* Details */
.sub-page .sub-details { margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e8ecf4; }
.sub-page .sub-restrict { display: flex; align-items: center; background: #f8fafc; border-radius: 8px; padding: 6px 10px; }

/* Empty */
.sub-page .sub-empty {
    text-align: center; padding: 48px 0; color: #64748b;
    background: #fff; border: 1px dashed #e8ecf4; border-radius: 14px;
}
.sub-page .sub-empty h5 { margin: 10px 0 4px; color: #1e293b; }
.sub-page .sub-empty p { margin: 0; }

/* Footer */
.sub-page .sub-footer {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 14px; font-size: 13px; color: #64748b;
}
.sub-page .sub-pager { display: flex; gap: 8px; }
.sub-page .sub-pager button {
    min-width: 34px; height: 34px; border: 1px solid #e8ecf4; background: #fff;
    color: #1e293b; border-radius: 8px; cursor: pointer; display: grid; place-items: center;
}
.sub-page .sub-pager button:hover:not(:disabled) { background: #eef2ff; }
.sub-page .sub-pager button:disabled { opacity: .45; cursor: not-allowed; }
.sub-page .sub-pager button.is-active { background: #4f46e5; color: #fff; border-color: #4f46e5; }

/* Responsive */
@media (max-width: 1399px) {
    .sub-page .sub-card__row { grid-template-columns: 220px minmax(0, 1fr) minmax(0, 1.2fr); }
    .sub-page .sub-card__side { grid-column: 1 / -1; flex-direction: row; align-items: center; justify-content: space-between; }
}
@media (max-width: 991px) {
    .sub-page .sub-stats { grid-template-columns: repeat(2, 1fr); }
    .sub-page .sub-filters { grid-template-columns: 1fr 1fr; }
    .sub-page .sub-filters__search { grid-column: 1 / -1; }
    .sub-page .sub-card__row { grid-template-columns: 1fr; }
    .sub-page .sub-card__media { height: 180px; }
    .sub-page .sub-card__metrics { padding: 12px 0; border: 0; border-top: 1px solid #e8ecf4; border-bottom: 1px solid #e8ecf4; }
}
@media (max-width: 575px) {
    .sub-page .sub-stats { grid-template-columns: 1fr; }
    .sub-page .sub-filters { grid-template-columns: 1fr; }
    .sub-page .sub-card__side { flex-direction: column; align-items: flex-start; min-width: 0; }
}
`;

const ViewSubscription = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');
    const animatedComponents = makeAnimated();
    const { access } = useSelector((state) => state.login);
    const { loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem("email");
    const [subscriptionAccess, setSubscriptionAccess] = useState([]);

    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("Subscription:")
            );
            setSubscriptionAccess(filteredAccess);
        }
    }, [access]);

    const [subscriptionList, setSubscriptionList] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editSubscription, setEditSubscription] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
    const [previewImages, setPreviewImages] = useState({
        banner: null,
        otherImage: null
    });
    const [formData, setFormData] = useState({
        title: '',
        subTitle: '',
        description: '',
        termsConditions: [],
        subscriptionWorking: [],
        applicableCategories: [],
        excludedDays: [],
        redemptionInterval: '',
        duration: '',
        price: '',
        taxPercent: '',
        banner: null,
        status: '',
        otherImage: null,
        excludedOutlets: [],
        subscriptionType: '',
        excludedProducts: [],
        excludedAddonValues: [],
        orderThreshold: ''
    });
    const daysOfWeek = [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday',
        'Friday', 'Saturday', 'Sunday'
    ];
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState('');
    const [newTerm, setNewTerm] = useState('');
    const [newWork, setNewWork] = useState('');

    const [categoryList, setCategoryList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [addonList, setAddonList] = useState([]);
    const [outletList, setOutletList] = useState([]);
    const [filteredAddonValues, setFilteredAddonValues] = useState([]);
    const [selectedAddonGroups, setSelectedAddonGroups] = useState([]);

    // Filters + pagination
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [outletFilter, setOutletFilter] = useState(null);
    const [page, setPage] = useState(1);

    // Fetch subscriptions
    const getSubscriptions = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("subscription", { headers });
            setSubscriptionList(response?.data?.data?.subscriptions || []);
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to fetch subscriptions", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const getCategoryList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("masters/category/get", { headers });
            if (response?.data?.status === true) {
                setCategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                toast.error(error.response.data.error, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const getOutletList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("outlet", { headers });
            if (response?.data?.status === true) {
                const filteredOutlets = response?.data?.data?.outlets.filter((act) => act?.status === "ACTIVE");
                setOutletList(filteredOutlets);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to fetch outlets", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const getProductList = async (categoryIds) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("product", { headers });
            if (response?.data?.status === true) {
                let allProducts = response?.data?.data?.products;
                allProducts = allProducts?.filter(product =>
                    categoryIds?.includes(product?.category?._id)
                );
                if (allProducts.length > 0) {
                    setProductList(allProducts);
                } else {
                    setProductList(response?.data?.data?.products);
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to fetch products", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const getAddonList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("addons?page=1&limit=50", { headers });
            if (response?.data?.status === true) {
                setAddonList(response?.data?.data?.addons);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to fetch addons", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    useEffect(() => {
        getCategoryList();
        getOutletList();
        getProductList();
        getAddonList();
    }, []);

    useEffect(() => {
        getSubscriptions();
    }, []);

    // Stats
    const stats = useMemo(() => ({
        total: subscriptionList.length,
        active: subscriptionList.filter(s => s?.status === 'ACTIVE').length,
        inactive: subscriptionList.filter(s => s?.status === 'INACTIVE').length,
        deleted: subscriptionList.filter(s => s?.status === 'DELETED').length
    }), [subscriptionList]);

    // Filtering
    const filteredList = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return subscriptionList.filter(s => {
            if (q && !(`${s?.title || ''} ${s?.subTitle || ''} ${s?.description || ''}`.toLowerCase().includes(q))) return false;
            if (statusFilter && s?.status !== statusFilter) return false;
            if (typeFilter && s?.subscriptionType !== typeFilter) return false;
            if (outletFilter && s?.excludedOutlets?.some(o => (o?._id || o) === outletFilter.value)) return false;
            return true;
        });
    }, [subscriptionList, searchText, statusFilter, typeFilter, outletFilter]);

    useEffect(() => {
        setPage(1);
    }, [searchText, statusFilter, typeFilter, outletFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
    const startIdx = (page - 1) * PAGE_SIZE;
    const pagedList = filteredList.slice(startIdx, startIdx + PAGE_SIZE);

    const clearFilters = () => {
        setSearchText('');
        setStatusFilter('');
        setTypeFilter('');
        setOutletFilter(null);
    };

    // Open edit modal and populate form
    const handleEdit = (subscription) => {
        const excludedAddonGroups = subscription?.excludedAddonValues?.map(value => ({
            _id: value.addonId._id,
            title: value.addonId.title,
            addonValues: value.addonId.addonValues
        })) || [];

        const filteredAddonValues = excludedAddonGroups.flatMap(group => {
            return group.addonValues?.map(val => ({
                value: {
                    addonId: group._id,
                    addValueId: val._id
                },
                label: `${group.title} - ${val.value}`
            }));
        });

        setEditSubscription(subscription);
        setFormData({
            title: subscription?.title,
            subTitle: subscription?.subTitle,
            description: subscription?.description,
            termsConditions: subscription?.termsConditions || [],
            subscriptionWorking: subscription?.subscriptionWorking || [],
            applicableCategories: subscription?.applicableCategories || [],
            excludedDays: subscription?.excludedDays || [],
            redemptionInterval: subscription?.redemptionInterval,
            duration: subscription?.duration,
            price: subscription?.price,
            status: subscription?.status,
            taxPercent: subscription?.taxPercent,
            subscriptionType: subscription?.subscriptionType,
            banner: null,
            otherImage: null,
            orderQuantity: subscription?.orderQuantity,
            excludedOutlets: subscription?.excludedOutlets || [],
            excludedProducts: subscription?.excludedProducts || [],
            excludedAddonValues: subscription?.excludedAddonValues?.map(val => ({
                addonId: val.addonId._id,
                addValueId: val.addValueId
            })) || [],
            excludedAddonGroups: excludedAddonGroups,
            orderThreshold: subscription?.orderThreshold || ''
        });

        setSelectedAddonGroups(excludedAddonGroups.map(group => ({
            value: group._id,
            label: group.title
        })));

        setFilteredAddonValues(filteredAddonValues);

        setPreviewImages({
            banner: subscription?.banner ? subscription?.banner : null,
            otherImage: subscription?.otherImage ? subscription?.otherImage : null
        });

        setShowEditModal(true);
    };

    const handleSelectChange = (fieldName, selectedOptions) => {
        if (fieldName === "applicableCategories") {
            const catIds = selectedOptions.map(option => option.value);
            getProductList(catIds);
        }

        let selectedItems = [];

        if (fieldName === 'applicableCategories') {
            selectedItems = selectedOptions
                ? categoryList.filter(category =>
                    selectedOptions.some(option => option.value === category._id)
                )
                : [];
        } else if (fieldName === 'excludedOutlets') {
            selectedItems = selectedOptions
                ? outletList.filter(outlet =>
                    selectedOptions.some(option => option.value === outlet._id)
                )
                : [];
        } else if (fieldName === 'excludedProducts') {
            selectedItems = selectedOptions
                ? productList.filter(product =>
                    selectedOptions.some(option => option.value === product._id)
                )
                : [];
        }

        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedItems
        }));
    };

    const handleCheckboxChange = (field, value, isChecked) => {
        setFormData(prev => ({
            ...prev,
            [field]: isChecked
                ? [...prev[field], value]
                : prev[field].filter(item => item !== value)
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files[0]
        }));

        if (files && files[0]) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImages(prev => ({
                    ...prev,
                    [name]: reader.result
                }));
            };
            reader.readAsDataURL(files[0]);
        }
    };

    const addTerm = () => {
        if (newTerm.trim()) {
            setFormData(prev => ({
                ...prev,
                termsConditions: [...prev.termsConditions, newTerm.trim()]
            }));
            setNewTerm('');
        }
    };

    const removeTerm = (index) => {
        setFormData(prev => ({
            ...prev,
            termsConditions: prev.termsConditions.filter((_, i) => i !== index)
        }));
    };

    const addWork = () => {
        if (newWork.trim()) {
            setFormData(prev => ({
                ...prev,
                subscriptionWorking: [...prev.subscriptionWorking, newWork.trim()]
            }));
            setNewWork('');
        }
    };

    const removeWork = (index) => {
        setFormData(prev => ({
            ...prev,
            subscriptionWorking: prev.subscriptionWorking.filter((_, i) => i !== index)
        }));
    };

    // Submit edited subscription
    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        setIsEditBtnLoading(true);
        try {
            const formDataToSend = new FormData();

            Object.keys(formData).forEach(key => {
                if (
                    key === 'applicableCategories' ||
                    key === 'excludedDays' ||
                    key === 'termsConditions' ||
                    key === 'subscriptionWorking' ||
                    key === 'excludedOutlets' ||
                    key === 'excludedProducts' ||
                    key === 'excludedAddonGroups'
                ) {
                    formData[key]?.forEach((item, index) => {
                        const value = typeof item === 'object' && item !== null ? item._id : item;
                        formDataToSend.append(`${key}[${index}]`, value);
                    });
                }
                else if (key === 'excludedAddonValues') {
                    formData[key]?.forEach((item, index) => {
                        formDataToSend.append(`${key}[${index}][addonId]`, item.addonId);
                        formDataToSend.append(`${key}[${index}][addValueId]`, item.addValueId);
                    });
                }
                else if (key === 'banner' || key === 'otherImage') {
                    if (formData[key]) {
                        formDataToSend.append(key, formData[key]);
                    }
                } else {
                    if (formData[key] !== null && formData[key] !== undefined) {
                        formDataToSend.append(key, formData[key]);
                    }
                }
            });

            if (editSubscription?._id) {
                formDataToSend.append('id', editSubscription._id);
            }

            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            };

            await axiosInstance.put(`subscription`, formDataToSend, { headers });

            toast.success("Subscription updated successfully!", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });

            setShowEditModal(false);
            getSubscriptions();
        } catch (error) {
            console.error('Error updating subscription:', error);
            if (error?.response) {
                let errorMessage = '';
                if (error.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
                }
                else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (error.response?.status === 400) {
                    errorMessage = error?.response?.data?.error;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.message;
                }

                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setIsEditBtnLoading(false);
        }
    };

    const toggleAccordion = (id) => {
        setActiveKey(activeKey === id ? null : id);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleDeleteClick = (subscription) => {
        setSubscriptionToDelete(subscription);
        setSubscriptionId(subscription?._id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async (e) => {
        if (subscriptionToDelete) {
            e.preventDefault();
            setIsDeleteLoading(true);
            try {
                const headers = { Authorization: `Bearer ${accessToken}` };
                const response = await axiosInstance.delete(
                    `subscription?id=${subscriptionId}`,
                    { headers }
                );
                if (response?.data?.status === true) {
                    toast.success(response?.data?.data?.message, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                    });
                    setShowDeleteModal(false);
                    getSubscriptions();
                }
            } catch (error) {
                if (error.response.status === 403) {
                    toast.error(error?.response?.data?.message, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                    });
                }
                if (error.response.status === 406) {
                    toast.error(error?.response?.data?.message, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                    });
                }

                if (error.response && error.response?.data?.status === false) {
                    toast.error(error.response.data.error, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                    });
                }
            } finally {
                setIsDeleteLoading(false);
            }

            setShowDeleteModal(false);
            setSubscriptionToDelete(null);
        }
    };

    const canView = (email === "contact@xpansecafe.com") || subscriptionAccess?.includes("Subscritpions:get");
    const canEdit = (email === "contact@xpansecafe.com") || subscriptionAccess?.includes("Subscritpions:update");
    const canDelete = (email === "contact@xpansecafe.com") || subscriptionAccess?.includes("Subscritpions:delete");

    const statCards = [
        { key: 'total', label: 'Total Subscriptions', value: stats.total, icon: <BoxSeam />, cls: 'total' },
        { key: 'active', label: 'Active', value: stats.active, icon: <Eye />, cls: 'active' },
        { key: 'inactive', label: 'Inactive', value: stats.inactive, icon: <PauseCircleFill />, cls: 'inactive' },
        { key: 'deleted', label: 'Deleted', value: stats.deleted, icon: <EyeSlash />, cls: 'deleted' },
    ];

    return (
        <React.Fragment>
            <style>{subStyles}</style>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div className="sub-page">

                    {/* Hero */}
                    <div className="sub-hero">
                        <div className="sub-hero__left">
                            <div className="sub-hero__icon"><Gem /></div>
                            <div>
                                <h4 className="sub-hero__title">Subscription Management</h4>
                                <p className="sub-hero__subtitle">View and manage all subscription plans</p>
                            </div>
                        </div>
                        <div className="sub-hero__actions">
                            <button
                                type="button"
                                className="sub-btn sub-btn--primary"
                                onClick={() => navigate('/subscription/add-subscription')}
                            >
                                <Plus size={20} /> Create Subscription
                            </button>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="sub-stats">
                        {statCards.map(card => (
                            <div key={card.key} className={`sub-stat sub-stat--${card.cls}`}>
                                <div className="sub-stat__icon">{card.icon}</div>
                                <div>
                                    <div className="sub-stat__value">{card.value}</div>
                                    <div className="sub-stat__label">{card.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filters */}
                    <div className="sub-filters">
                        <div className="sub-filters__search">
                            <Search />
                            <input
                                type="text"
                                placeholder="Search by title or description..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                        <select className="sub-filters__select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                        </select>
                        <select className="sub-filters__select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="">All Types</option>
                            <option value="DAILY">Daily</option>
                            <option value="HOURLY">Hourly</option>
                            <option value="UNLIMITED">Unlimited</option>
                        </select>
                        <div className="sub-filters__outlet">
                            <Select
                                isClearable
                                options={outletList?.map(o => ({ value: o?._id, label: o?.name }))}
                                value={outletFilter}
                                onChange={setOutletFilter}
                                placeholder="Filter by Outlet"
                                classNamePrefix="sub-select"
                            />
                        </div>
                        <button type="button" className="sub-btn sub-btn--soft" onClick={clearFilters}>
                            <ArrowRepeat size={16} /> Clear
                        </button>
                    </div>

                    {/* List */}
                    {filteredList.length === 0 ? (
                        <div className="sub-empty">
                            <CupHot size={44} />
                            <h5>No subscriptions found</h5>
                            <p>Try changing the filters or create a new subscription plan</p>
                        </div>
                    ) : (
                        <div className="sub-list">
                            {pagedList.map((subscription) => (
                                <div className="sub-card" key={subscription?._id}>
                                    <div className="sub-card__row">
                                        {/* Banner */}
                                        <div className="sub-card__media">
                                            {subscription?.banner ? (
                                                <img src={subscription.banner} alt={subscription?.title} />
                                            ) : (
                                                <div className="sub-card__media-fallback"><CupHot size={32} /></div>
                                            )}
                                            <span className={`sub-card__status sub-card__status--${subscription?.status?.toLowerCase()}`}>
                                                {subscription?.status?.toLowerCase()}
                                            </span>
                                        </div>

                                        {/* Info */}
                                        <div className="sub-card__info">
                                            <h5 className="sub-card__title">{subscription?.title}</h5>
                                            {subscription?.subTitle && <p className="sub-card__subtitle">{subscription?.subTitle}</p>}
                                            {subscription?.description && <p className="sub-card__desc">{subscription?.description}</p>}
                                            <div className="sub-card__tags">
                                                {subscription?.subscriptionType === "HOURLY" ? (
                                                    <span className="sub-tag sub-tag--blue"><Clock size={13} /> Hourly Plan</span>
                                                ) : subscription?.subscriptionType === "UNLIMITED" ? (
                                                    <span className="sub-tag sub-tag--indigo"><Clock size={13} /> Unlimited Plan</span>
                                                ) : (
                                                    <span className="sub-tag sub-tag--amber"><Calendar size={13} /> Daily Plan</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Metrics */}
                                        <div className="sub-card__metrics">
                                            <div className="sub-metric">
                                                <CurrencyRupee />
                                                <div><strong>₹{subscription?.price}</strong><span>Price</span></div>
                                            </div>
                                            <div className="sub-metric">
                                                <Calendar />
                                                <div><strong>{subscription?.duration} days</strong><span>Duration</span></div>
                                            </div>
                                            <div className="sub-metric">
                                                <BoxSeam />
                                                <div><strong>{subscription?.orderQuantity}</strong><span>Order Quantity</span></div>
                                            </div>
                                            <div className="sub-metric">
                                                <HourglassSplit />
                                                <div><strong>{subscription?.orderThreshold}</strong><span>Order Threshold</span></div>
                                            </div>
                                            <div className="sub-metric sub-metric--wide">
                                                <Clock />
                                                <div><strong>{subscription?.redemptionInterval} mins</strong><span>Redemption Interval</span></div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="sub-card__side">
                                            <div className="sub-card__valid">
                                                Valid:
                                                <span className="sub-pill-green">
                                                    {subscription?.expiryDate ? formatDate(subscription.expiryDate) : 'No expiry'}
                                                </span>
                                            </div>
                                            <div className="sub-card__actions">
                                                {canView && (
                                                    <button
                                                        type="button"
                                                        className="sub-btn sub-btn--outline"
                                                        onClick={() => toggleAccordion(subscription?._id)}
                                                    >
                                                        {activeKey === subscription?._id ? 'Hide Details' : 'View Details'}
                                                    </button>
                                                )}
                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        className="sub-icon-btn"
                                                        aria-label="Edit subscription"
                                                        onClick={() => handleEdit(subscription)}
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        type="button"
                                                        className="sub-icon-btn sub-icon-btn--danger"
                                                        aria-label="Delete subscription"
                                                        onClick={() => handleDeleteClick(subscription)}
                                                    >
                                                        <Trash size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <Accordion activeKey={activeKey === subscription._id ? '0' : ''}>
                                        <Accordion.Collapse eventKey="0">
                                            <div className="sub-details">
                                                <Row>
                                                    <Col md={6}>
                                                        <h6 className="fw-semibold mb-3">Subscription Details</h6>
                                                        <Table borderless size="sm">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text-muted" width="40%">Created At:</td>
                                                                    <td>{formatDate(subscription.createdAt)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-muted">Last Updated:</td>
                                                                    <td>{formatDate(subscription.updatedAt)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-muted">Subscription Type:</td>
                                                                    <td className="text-capitalize">{subscription.subscriptionType.toLowerCase()}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-muted">Tax Percentage:</td>
                                                                    <td>{subscription.taxPercent}%</td>
                                                                </tr>
                                                                {subscription?.applicableCategories?.length > 0 && (
                                                                    <tr>
                                                                        <td className="text-muted">Applicable Categories:</td>
                                                                        <td>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="categories-tooltip">
                                                                                        {subscription?.applicableCategories?.map(category => category?.name).join(', ')}
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="fw-semibold">
                                                                                    {subscription?.applicableCategories?.length} Categories
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </Col>
                                                    <Col md={6}>
                                                        <h6 className="fw-semibold mb-3">Restrictions</h6>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {subscription?.excludedDays?.length > 0 && (
                                                                <div className="sub-restrict">
                                                                    <CalendarEvent className="text-danger me-2" />
                                                                    <small>Excluded Days: {subscription.excludedDays.join(', ')}</small>
                                                                </div>
                                                            )}
                                                            {subscription?.excludedOutlets?.length > 0 && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="outlets-tooltip">
                                                                            {subscription.excludedOutlets.map(outlet => outlet.name).join(', ')}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className="sub-restrict">
                                                                        <ShopWindow className="text-danger me-2" />
                                                                        <small>{subscription.excludedOutlets?.length} Outlets Excluded</small>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            )}
                                                            {subscription?.excludedProducts?.length > 0 && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="products-tooltip">
                                                                            {subscription?.excludedProducts?.map(product => product.name).join(', ')}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className="sub-restrict">
                                                                        <CartPlus className="text-danger me-2" />
                                                                        <small>{subscription?.excludedProducts?.length} Products Excluded</small>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            )}
                                                            {subscription?.excludedAddonGroups?.length > 0 && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="addon-groups-tooltip">
                                                                            {subscription?.excludedAddonGroups?.map(group => group.title).join(', ')}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className="sub-restrict">
                                                                        <ListCheck className="text-danger me-2" />
                                                                        <small>{subscription?.excludedAddonGroups?.length} Addon Groups Excluded</small>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            )}
                                                            {subscription?.excludedAddonValues?.length > 0 && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="addon-values-tooltip">
                                                                            {subscription?.excludedAddonValues?.map(val => {
                                                                                const group = val.addonId;
                                                                                const value = group?.addonValues?.find(v => v._id === val.addValueId);
                                                                                return `${group?.title || 'Unknown'} - ${value?.value || 'Unknown'}`;
                                                                            }).join(', ')}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className="sub-restrict">
                                                                        <ListCheck className="text-danger me-2" />
                                                                        <small>{subscription?.excludedAddonValues?.length} Addon Values Excluded</small>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            )}
                                                        </div>

                                                        <h6 className="fw-semibold mt-3 mb-2">Terms & Conditions</h6>
                                                        <ul className="list-unstyled small">
                                                            {subscription?.termsConditions?.map((term, index) => (
                                                                <li key={index} className="mb-1">
                                                                    <CheckCircle className="text-success me-2" size={14} />
                                                                    {term}
                                                                </li>
                                                            ))}
                                                        </ul>

                                                        <h6 className="fw-semibold mt-3 mb-2">How Does It Works</h6>
                                                        <ul className="list-unstyled small">
                                                            {subscription?.subscriptionWorking?.map((work, index) => (
                                                                <li key={index} className="mb-1">
                                                                    <CheckCircle className="text-success me-2" size={14} />
                                                                    {work}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Accordion.Collapse>
                                    </Accordion>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Footer / pagination */}
                    {filteredList.length > 0 && (
                        <div className="sub-footer">
                            <span>
                                Showing {startIdx + 1} to {Math.min(startIdx + PAGE_SIZE, filteredList.length)} of {filteredList.length} entries
                            </span>
                            <div className="sub-pager">
                                <button type="button" aria-label="Previous page" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                    <ChevronLeft />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={p === page ? 'is-active' : ''}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button type="button" aria-label="Next page" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                    <ChevronRight />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Subscription Modal */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="xl"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="h4 fw-semibold text-primary">UPDATE SUBSCRIPTION PLAN</Modal.Title>
                </Modal.Header>
                <hr />

                <Modal.Body className="pt-1">
                    <Form onSubmit={handleSubmitEdit}>
                        {/* Section 1: Basic Information */}
                        <div className="mb-4">
                            <h6 className="mb-3 text-muted fw-semibold">BASIC INFORMATION</h6>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group controlId="title">
                                        <Form.Label className="fw-semibold">Title<span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="subTitle">
                                        <Form.Label className="fw-semibold">Subtitle <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subTitle"
                                            value={formData.subTitle}
                                            onChange={handleInputChange}
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <Form.Group controlId="description">
                                        <Form.Label className="fw-semibold">Description <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs="6" lg="3">
                                    <Form.Label className="fw-semibold">
                                        Subscription Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select className='w-100' id="subscriptionType" name="subscriptionType" value={formData?.subscriptionType} onChange={handleInputChange}>
                                        <option value="" disabled>Select Subscription Type</option>
                                        <option value="DAILY">DAILY</option>
                                        <option value="HOURLY">HOURLY</option>
                                        <option value="UNLIMITED">UNLIMITED</option>
                                    </Form.Select>
                                </Col>
                                <Col xs="6" lg="3">
                                    <Form.Label className="fw-semibold">
                                        Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select className='w-100' id="status" name="status" value={formData?.status} onChange={handleInputChange}>
                                        <option value="" disabled>Select Status</option>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>How Does It Works <span className='text-danger'>*</span></Form.Label>

                                        <div className="mb-3">
                                            <div className="input-group">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Add the workings of the subscription"
                                                    value={newWork}
                                                    onChange={(e) => setNewWork(e.target.value)}
                                                />
                                                <Button variant="primary" onClick={addWork}>
                                                    Add
                                                </Button>
                                            </div>
                                        </div>

                                        {formData?.subscriptionWorking?.length > 0 && (
                                            <ListGroup>
                                                {formData?.subscriptionWorking?.map((work, index) => (
                                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                                        {work}
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => removeWork(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Terms & Conditions <span className='text-danger'>*</span></Form.Label>
                                        <div className="d-flex mb-2">
                                            <Form.Control
                                                type="text"
                                                value={newTerm}
                                                onChange={(e) => setNewTerm(e.target.value)}
                                                placeholder="Enter term..."
                                                className="py-2"
                                            />
                                            <Button
                                                variant="outline-primary"
                                                onClick={addTerm}
                                                className="ms-2 px-4"
                                                disabled={!newTerm.trim()}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                        <div className="border rounded p-3 bg-light" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {formData.termsConditions.length > 0 ? (
                                                <ListGroup variant="flush">
                                                    {formData.termsConditions.map((term, index) => (
                                                        <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center px-0 py-2">
                                                            <span className="text-break">{term}</span>
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                onClick={() => removeTerm(index)}
                                                                className="text-danger p-0"
                                                                aria-label="Remove term"
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </Button>
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            ) : (
                                                <div className="text-center text-muted py-3">No terms added yet</div>
                                            )}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        <hr />

                        {/* Section 2: Pricing & Duration */}
                        <div className="mb-4">
                            <h6 className="mb-3 text-muted fw-semibold">PRICING & DURATION</h6>
                            <Row className="g-3">
                                <Col md={4}>
                                    <Form.Group controlId="price">
                                        <Form.Label className="fw-medium">Price (₹)<span className="text-danger">*</span></Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>₹</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                name="price"
                                                value={formData.price}
                                                onChange={handleInputChange}
                                                required
                                                className="py-2"
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="duration">
                                        <Form.Label className="fw-semibold">Duration (days)<span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            step="any"
                                            name="duration"
                                            value={formData.duration}
                                            onChange={handleInputChange}
                                            required
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="redemptionInterval">
                                        <Form.Label className="fw-semibold">Redemption Interval (mins)<span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            name="redemptionInterval"
                                            value={formData.redemptionInterval}
                                            onChange={handleInputChange}
                                            required
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="taxPercent">
                                        <Form.Label className="fw-semibold">Tax (%)<span className="text-danger">*</span></Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="any"
                                                name="taxPercent"
                                                value={formData.taxPercent}
                                                onChange={handleInputChange}
                                                required
                                                className="py-2"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Order Quantity <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="any"
                                            name="orderQuantity"
                                            placeholder="Enter maximum orders a user can place (0 = no limit)"
                                            value={formData.orderQuantity}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="orderThreshold">
                                        <Form.Label className="fw-semibold">Order Threshold</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            name="orderThreshold"
                                            value={formData.orderThreshold}
                                            onChange={handleInputChange}
                                            placeholder="Enter minimum orders required (0 = no limit)"
                                            className="py-2"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        <hr />

                        {/* Section 3: Applicability */}
                        <div className="mb-4">
                            <h6 className="mb-3 text-muted fw-semibold">APPLICABILITY</h6>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Applicable Categories <span className='text-danger'>*</span></Form.Label>
                                        <Select
                                            components={animatedComponents}
                                            isMulti
                                            options={categoryList?.map(category => ({
                                                value: category?._id,
                                                label: category?.name
                                            }))}
                                            value={formData?.applicableCategories?.map(cat => ({
                                                value: cat._id,
                                                label: cat.name
                                            }))}
                                            onChange={(selectedOptions) => handleSelectChange('applicableCategories', selectedOptions)}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select categories..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Exclude Products</Form.Label>
                                        <Select
                                            components={animatedComponents}
                                            isMulti
                                            options={productList?.map(product => ({
                                                value: product?._id,
                                                label: product?.name
                                            }))}
                                            value={formData?.excludedProducts?.map(product => ({
                                                value: product._id,
                                                label: product.name
                                            }))}
                                            onChange={(selectedOptions) => handleSelectChange('excludedProducts', selectedOptions)}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select products to exclude..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Exclude Addon Groups</Form.Label>
                                        <Select
                                            components={animatedComponents}
                                            isMulti
                                            options={addonList.map(addon => ({
                                                value: addon._id,
                                                label: addon.title
                                            }))}
                                            value={formData.excludedAddonGroups?.map(group => ({
                                                value: group._id,
                                                label: group.title
                                            }))}
                                            onChange={(selectedOptions) => {
                                                const selectedGroups = addonList.filter(addon =>
                                                    selectedOptions?.some(option => option.value === addon._id)
                                                );

                                                setFormData(prev => ({
                                                    ...prev,
                                                    excludedAddonGroups: selectedGroups || []
                                                }));

                                                const addonValuesOptions = selectedGroups?.flatMap(group => {
                                                    return group.addonValues?.map(val => ({
                                                        value: {
                                                            addonId: group._id,
                                                            addValueId: val._id
                                                        },
                                                        label: `${group.title} - ${val.value}`
                                                    }));
                                                });

                                                setFilteredAddonValues(addonValuesOptions || []);
                                            }}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select addon groups to exclude..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Exclude Addon Values</Form.Label>
                                        <Select
                                            components={animatedComponents}
                                            isMulti
                                            options={filteredAddonValues}
                                            value={formData.excludedAddonValues?.map(val => ({
                                                value: val,
                                                label: `${addonList.find(a => a._id === val.addonId)?.title || ''} - ${addonList
                                                        .find(a => a._id === val.addonId)
                                                        ?.addonValues.find(v => v._id === val.addValueId)?.value || ''
                                                    }`
                                            }))}
                                            onChange={(selectedOptions) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    excludedAddonValues: selectedOptions?.map(option => option.value) || []
                                                }));
                                            }}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select addon values to exclude..."
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => `${option.value.addonId}-${option.value.addValueId}`}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Exclude Outlets</Form.Label>
                                        <Select
                                            components={animatedComponents}
                                            isMulti
                                            options={outletList?.map(outlet => ({
                                                value: outlet?._id,
                                                label: outlet?.name
                                            }))}
                                            value={formData?.excludedOutlets?.map(outlet => ({
                                                value: outlet._id,
                                                label: outlet.name
                                            }))}
                                            onChange={(selectedOptions) => handleSelectChange('excludedOutlets', selectedOptions)}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select outlets to exclude..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Exclude Days (Optional)</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {daysOfWeek.map(day => (
                                                <Form.Check
                                                    key={day}
                                                    type="checkbox"
                                                    id={`day-${day}`}
                                                    label={day}
                                                    checked={formData.excludedDays.includes(day)}
                                                    onChange={(e) => handleCheckboxChange('excludedDays', day, e.target.checked)}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        <hr />

                        {/* Section 4: Images */}
                        <div className="mb-4">
                            <h6 className="mb-3 text-muted fw-semibold">IMAGES</h6>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group controlId="banner">
                                        <Form.Label className="fw-semibold">Banner Image <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control
                                            type="file"
                                            name="banner"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            className="py-2"
                                        />
                                        {previewImages?.banner && (
                                            <div className="mt-3 text-center">
                                                <h6 className="small text-muted">Preview</h6>
                                                <img
                                                    src={previewImages?.banner}
                                                    alt="Banner preview"
                                                    className="img-fluid rounded border"
                                                    style={{ maxHeight: '120px' }}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="otherImage">
                                        <Form.Label className="fw-semibold">Other Image</Form.Label>
                                        <Form.Control
                                            type="file"
                                            name="otherImage"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            className="py-2"
                                        />
                                        {previewImages.otherImage && (
                                            <div className="mt-3 text-center">
                                                <h6 className="small text-muted">Preview</h6>
                                                <img
                                                    src={previewImages.otherImage}
                                                    alt="Other image preview"
                                                    className="img-fluid rounded border"
                                                    style={{ maxHeight: '120px' }}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Footer Buttons */}
                        <div className="d-flex justify-content-end gap-3 pt-3 border-top">
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowEditModal(false)}
                                className="px-4"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                className="px-4"
                            >
                                {isEditBtnLoading ? <Spinner size='sm' /> : "Save Changes"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Delete Subscription Modal */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                loading={isDeleteLoading}
                title={`Delete "${subscriptionToDelete?.title}"?`}
                message={`You're about to permanently delete the "${subscriptionToDelete?.title}" subscription and all its data. This action cannot be undone.`}
            />
        </React.Fragment>
    );
};

export default ViewSubscription;